import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { dropTask, didCancel } from 'ember-concurrency';
import { waitFor } from '@ember/test-waiters';
import BaseController from 'uplisting-frontend/pods/base/controller';
import { FilterId } from 'uplisting-frontend/utils/filters/abstract';
import {
  type DisputesFilter,
  type Filter,
  getFilterQueryParamName,
} from 'uplisting-frontend/utils/filters/types';
import { searchOptions } from 'uplisting-frontend/utils/search';
import type { ModelRegistry } from 'ember-data/types/registries/model';

const SEARCH_FIELDS = ['booking.guestName', 'booking.property.nickname'];
const PAGE_SIZE = 20;

interface IDisputesRequestMeta {
  meta?: {
    total?: number;
    total_pages?: number;
  };
}

export default class ProtectionDisputesController extends BaseController {
  @service('repositories/dispute')
  disputeRepository!: Services['repositories/dispute'];

  @service('filters') filtersService!: Services['filters'];

  queryParams = ['propertyIds', 'statuses', 'page'];

  @tracked disputes: ModelRegistry['dispute'][] = [];
  @tracked propertyIds: string[] = [];
  @tracked statuses: string[] = [];
  @tracked searchQuery = '';
  @tracked page = 1;
  @tracked totalPages = 0;

  #filters?: DisputesFilter[];
  #builtFrom?: string;

  // Rebuilt only when the query params change from outside this controller —
  // Back, Forward, or a pasted URL. Rebuilding on the host's own edit replaced
  // the controls mid-interaction, which shut an open dropdown and flashed the
  // list behind it.
  get filterList(): DisputesFilter[] {
    const signature = this.#queryParamSignature;

    if (!this.#filters || this.#builtFrom !== signature) {
      this.#filters = [
        this.filtersService.createFilterFor(
          FilterId.property,
          this.propertyIds,
        ) as DisputesFilter,
        this.filtersService.createFilterFor(
          FilterId.disputeStatus,
          this.statuses,
        ) as DisputesFilter,
      ];

      this.#builtFrom = signature;
    }

    return this.#filters;
  }

  public resetFilters(): void {
    this.#filters = undefined;
    this.#builtFrom = undefined;
  }

  get #queryParamSignature(): string {
    return JSON.stringify([
      [...this.propertyIds].sort(),
      [...this.statuses].sort(),
    ]);
  }

  @cached
  get isFiltered(): boolean {
    return (
      this.searchQuery !== '' ||
      this.propertyIds.length > 0 ||
      this.statuses.length > 0
    );
  }

  @cached
  get disputesWithId(): ModelRegistry['dispute'][] {
    return this.disputes.filter((dispute) => dispute.id);
  }

  @cached
  get visibleDisputes(): ModelRegistry['dispute'][] {
    return searchOptions(this.disputesWithId, this.searchQuery, SEARCH_FIELDS);
  }

  @cached
  get hasDisputes(): boolean {
    return this.visibleDisputes.length !== 0;
  }

  @action
  handleSearchInput(event: Event): void {
    this.searchQuery = (event.target as HTMLInputElement).value;
  }

  @action
  @waitFor
  public async handleFilterUpdate(filter: Filter): Promise<void> {
    const queryParam = getFilterQueryParamName(filter);

    this[queryParam] = filter.toQuery();
    this.#builtFrom = this.#queryParamSignature;

    this.page = 1;

    await this.fetchData();
  }

  @action
  @waitFor
  public async handlePageChange(page: number): Promise<void> {
    if (this.fetchDataTask.isRunning) {
      return;
    }

    this.page = page;

    await this.fetchData();
  }

  @action
  @waitFor
  public async fetchData(): Promise<void> {
    try {
      await this.fetchDataTask.perform();
    } catch (e) {
      if (!didCancel(e)) {
        throw e;
      }
    }
  }

  public fetchDataTask = dropTask(async () => {
    let data = await this.queryDisputes();

    const { meta } = data as unknown as IDisputesRequestMeta;

    this.totalPages = meta?.total_pages ?? 0;

    if (this.totalPages > 0 && this.page > this.totalPages) {
      this.page = this.totalPages;

      data = await this.queryDisputes();
    }

    this.disputes = data.slice();
  });

  @action
  handleOpenDispute(dispute: ModelRegistry['dispute']): void {
    this.router.transitionTo('protection.disputes.dispute', dispute.id);
  }

  private async queryDisputes() {
    return this.disputeRepository.query({
      property_ids: this.propertyIds,
      statuses: this.statuses,
      page: {
        number: this.page,
        size: PAGE_SIZE,
      },
    });
  }
}
